import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Pencil } from 'lucide-react';
import { useProfile, useUserListings, useDeleteListing } from '@/hooks/useApi';
import { useAuthStore } from '@/store';
import { Listing } from '@/types';
import { formatPrice } from '@/utils';
import CardImage from '@/components/CardImage';

export default function UserListingsPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.user);
  const { data: profile, isLoading: profileLoading } = useProfile(id);
  const { data: listings, isLoading: listingsLoading } = useUserListings(id);
  const { mutate: deleteListing } = useDeleteListing();

  const isOwnProfile = !id;
  const activeListings = Array.isArray(listings)
    ? listings
        .filter((listing: Listing) => listing.status === 'active')
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    : [];

  if (profileLoading) {
    return (
      <div className="container-custom py-12">
        <div className="skeleton h-12 w-64 rounded-xl mb-6" />
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container-custom py-24 text-center">
        <h2 className="text-2xl font-bold mb-2">User not found</h2>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <div className="container-custom py-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <Link
              to={isOwnProfile ? '/profile' : `/profile/${profile.id}`}
              className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:text-brand-400 mb-3"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to profile
            </Link>
            <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-zinc-100">
              {isOwnProfile ? 'Your Listings' : `${profile.username}'s Listings`}
            </h1>
            <p className="text-gray-500 dark:text-zinc-500 mt-1">
              {activeListings.length} active listing{activeListings.length === 1 ? '' : 's'}
            </p>
          </div>

          {isOwnProfile && (
            <Link to="/listings/create" className="btn-primary py-2">
              + New Listing
            </Link>
          )}
        </div>

        {listingsLoading ? (
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)}
          </div>
        ) : activeListings.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-dashed border-gray-200 dark:border-zinc-800 p-10 text-center">
            <p className="text-gray-500 dark:text-zinc-500 text-sm">No active listings.</p>
            {isOwnProfile && (
              <Link to="/listings/create" className="text-brand-600 dark:text-brand-400 font-medium text-sm hover:underline inline-block mt-2">
                Create your first listing →
              </Link>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
            <div className="space-y-3">
              {activeListings.map((listing: Listing) => (
                <div key={listing.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors group">
                  <Link to={`/listings/${listing.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="h-14 w-10 rounded-lg overflow-hidden flex-shrink-0">
                      <CardImage src={listing.image_urls?.[0]} alt={listing.card?.name} cardName={listing.card?.name} game={listing.card?.game} className="w-full h-full" compact />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{listing.card?.name ?? listing.title}</p>
                      <p className="text-xs text-gray-500 dark:text-zinc-500">{listing.condition}</p>
                    </div>
                    {listing.listing_type === 'auction' ? (
                      <span className="text-sm font-bold text-orange-600">
                        {listing.current_bid ? `Top: ${formatPrice(listing.current_bid)}` : `Start: ${formatPrice(listing.starting_price ?? listing.price)}`}
                      </span>
                    ) : (listing.listing_type as string) === 'trade' ? (
                      <span className="text-xs font-semibold text-purple-600">Trade</span>
                    ) : (
                      <span className="text-sm font-bold text-brand-600 dark:text-brand-400">{formatPrice(listing.price)}</span>
                    )}
                  </Link>
                  {isOwnProfile && currentUser?.id === profile.id && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button
                        onClick={() => navigate(`/listings/${listing.id}/edit`)}
                        className="text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 p-1 transition-colors"
                        title="Edit listing"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => { if (window.confirm('Delete this listing?')) deleteListing(listing.id); }}
                        className="text-red-400 hover:text-red-600 p-1 transition-colors"
                        title="Delete listing"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
